import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Group, Rect, Text } from 'react-konva'
import { useDispatch } from 'react-redux'

import { useAppSelector } from '@/store'
import type { PLACEMENT } from '@/store/appSlice'
import { setSelectedColor } from '@/store/appSlice'
import {
  addColorCheckerElement,
  removeColorCheckerElement,
  removeSelectedColorCheckerShade,
  setSelectedColorCheckerShade,
} from '@/store/digitalTryInSlice'

import { getAverageColor } from './utils'

const Element = ({
  hiddenCanvasRef,
  placement,
  rect,
}: {
  hiddenCanvasRef: any
  placement: PLACEMENT
  rect: any
}) => {
  const dispatch = useDispatch()

  const [localWidth, setLocalWidth] = useState(rect.width)
  const [isResizing, setIsResizing] = useState(false)

  const [scale, setScale] = useState(1)
  const debounceRef = useRef<number | null>(null)
  const scaleResetRef = useRef<number | null>(null)

  useEffect(() => {
    setLocalWidth(rect.width)
  }, [rect.width])

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      if (scaleResetRef.current) {
        clearTimeout(scaleResetRef.current)
      }
    }
  }, [])

  const dispatchResize = (newSize: number) => {
    const { color, labColor, isLight } = getAverageColor(
      rect.x,
      rect.y,
      newSize,
      newSize,
      hiddenCanvasRef,
    )

    const updatedRect = {
      ...rect,
      width: newSize,
      height: newSize,
      color,
      labColor,
      isLight,
      label: `L:${labColor.L}, a:${labColor.a}, b:${labColor.b}`,
    }

    dispatch(addColorCheckerElement(updatedRect))
    dispatch(setSelectedColor(labColor))
    dispatch(
      setSelectedColorCheckerShade({
        [placement]: {
          [rect.region]: labColor,
        },
      }),
    )
  }

  const handleResizeClick = (delta: number) => {
    const currentSize = localWidth
    const newSize = Math.min(Math.max(currentSize + delta, 40), 80)

    if (newSize !== currentSize) {
      setLocalWidth(newSize)
      setIsResizing(true)

      setScale(1.12)
      if (scaleResetRef.current) clearTimeout(scaleResetRef.current)
      scaleResetRef.current = window.setTimeout(() => {
        setScale(1)
        setIsResizing(false)
      }, 120)

      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = window.setTimeout(() => {
        dispatchResize(newSize)
        debounceRef.current = null
      }, 300)
    }
  }

  const updateRectanglePosition = (e: any, rect: any) => {
    const node = e.target

    const newCenterX = node.x() + localWidth / 2
    const newCenterY = node.y() + localWidth / 2

    const { color, labColor } = getAverageColor(
      node.x(),
      node.y(),
      localWidth,
      localWidth,
      hiddenCanvasRef,
    )

    const updatedRect = {
      ...rect,
      centerX: newCenterX,
      centerY: newCenterY,
      width: localWidth,
      height: localWidth,
      color,
      labColor,
      label: `L:${labColor.L}, a:${labColor.a}, b:${labColor.b}`,
    }

    dispatch(addColorCheckerElement(updatedRect))
    dispatch(setSelectedColor(labColor))
    dispatch(
      setSelectedColorCheckerShade({
        [placement]: {
          [rect.region]: labColor,
        },
      }),
    )
  }

  const handleRectangleDragEnd = (e: any, rect: any) => {
    updateRectanglePosition(e, rect)
  }

  const handleRectangleDragMove = (e: any, rect: any) => {
    updateRectanglePosition(e, rect)
  }

  const handleRemove = (rect: any) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }

    dispatch(removeColorCheckerElement({ placement, region: rect.region }))
    dispatch(
      removeSelectedColorCheckerShade({ placement, region: rect.region }),
    )
  }

  const displayWidth = localWidth * scale
  const displayHeight = localWidth * scale
  const displayX = rect.centerX - displayWidth / 2
  const displayY = rect.centerY - displayHeight / 2

  const hexToRgba = (hex: string | undefined, alpha = 0.22) => {
    if (!hex) return `rgba(33,33,33,${alpha})`

    const clean = hex.replace('#', '')
    const hexValue =
      clean.length === 3
        ? clean
            .split('')
            .map((c) => c + c)
            .join('')
        : clean

    const v = Number.parseInt(hexValue, 16)

    const r = (v >> 16) & 255
    const g = (v >> 8) & 255
    const b = v & 255

    return `rgba(${r},${g},${b},${alpha})`
  }

  const mainFill = isResizing ? hexToRgba(rect.color, 0.22) : undefined

  return (
    <React.Fragment>
      <Group>
        {/* Main draggable square */}
        <Rect
          x={displayX}
          y={displayY}
          width={displayWidth}
          height={displayHeight}
          stroke="white"
          strokeWidth={2}
          cornerRadius={4}
          draggable
          scaleX={scale}
          scaleY={scale}
          fill={mainFill}
          onDragStart={(e) => {
            e.cancelBubble = true
            e.evt.stopPropagation()
          }}
          onDragMove={(e) => {
            e.cancelBubble = true
            e.evt.stopPropagation()
            handleRectangleDragMove(e, rect)
          }}
          onDragEnd={(e) => {
            e.cancelBubble = true
            e.evt.stopPropagation()
            handleRectangleDragEnd(e, rect)
          }}
        />

        {/* Crosshair grid inside */}
        <Group>
          {/* vertical line */}
          <Rect
            x={displayX + displayWidth / 2 - 0.5}
            y={displayY}
            width={1}
            height={displayHeight}
            fill="white"
            scaleX={scale}
            scaleY={scale}
          />
          {/* horizontal line */}
          <Rect
            x={displayX}
            y={displayY + displayHeight / 2 - 0.5}
            width={displayWidth}
            height={1}
            fill="white"
            scaleX={scale}
            scaleY={scale}
          />
          {/* center dot */}
          <Rect
            x={displayX + displayWidth / 2 - 3}
            y={displayY + displayHeight / 2 - 3}
            width={6}
            height={6}
            cornerRadius={2}
            fill="white"
            scaleX={scale}
            scaleY={scale}
          />
        </Group>

        {/* Control buttons above */}
        <Group x={displayX + displayWidth / 2 - 22} y={displayY - 30}>
          {/* Minus button */}
          <Rect
            width={20}
            height={20}
            fill="rgba(33, 33, 33, 0.80)"
            cornerRadius={4}
            onClick={() => handleResizeClick(-20)}
          />
          <Text
            text="-"
            x={8}
            y={4}
            fontSize={14}
            fill={localWidth <= 40 ? 'rgba(200,200,200,0.5)' : 'white'}
            listening={false}
            fontWeight="boldest"
          />

          {/* Plus button */}
          <Group x={25}>
            <Rect
              width={20}
              height={20}
              fill="rgba(33, 33, 33, 0.80)"
              cornerRadius={4}
              onClick={() => handleResizeClick(20)}
            />
            <Text
              text="+"
              x={6}
              y={4}
              fontSize={14}
              fill={localWidth >= 80 ? 'rgba(200,200,200,0.5)' : 'white'}
              listening={false}
              fontWeight="boldest"
            />
          </Group>
        </Group>

        <Group x={displayX + displayWidth - 8} y={displayY - 8}>
          <Rect
            width={16}
            height={16}
            fill="#E46962"
            cornerRadius={11}
            onClick={() => handleRemove(rect)}
          />
          <Text
            text="×"
            x={4}
            y={2}
            fontSize={14}
            fill="white"
            listening={false}
          />
        </Group>
      </Group>
    </React.Fragment>
  )
}

const KonvaColorChecker = ({
  hiddenCanvasRef,
  placement,
}: {
  hiddenCanvasRef: any
  placement: PLACEMENT
}) => {
  const colorCheckerElements = useAppSelector(
    (state) => state.digitalTryIn.colorCheckerElements,
  )

  const elements = useMemo(() => {
    return Object.values(colorCheckerElements?.[placement] || {}) || []
  }, [colorCheckerElements, placement])
  return (
    <>
      {elements.map((rect: any) => {
        return (
          <Element
            hiddenCanvasRef={hiddenCanvasRef}
            placement={placement}
            rect={rect}
            key={rect.region}
          />
        )
      })}
    </>
  )
}

export default KonvaColorChecker
